import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import ReactPaginate from "react-paginate";
import { FaBorderAll, FaExpandAlt, FaLayerGroup, FaListUl, FaQuoteLeft, FaSearch, FaSlidersH } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import Product from "../../home/Products/Product";
import {
  paginationItems,
  paginationItems2,
  paginationItems3,
  paginationMeta,
  paginationMeta2,
  paginationMeta3,
  loadOffer,
  loadProducts,
  loadNews,
} from "../../../constants/products";
import { addToCart } from "../../../redux/orebiSlice";
import ProductTable from "./ProductTable";
import { UserConfigContext } from "../../../context/UserConfigContext";
import { useFavorites } from "../../../context/FavoritesContext";

const Items = ({ products, viewType, hoveredItem, setHoveredItem, onProductsRefresh }) => {
  const [quantities, setQuantities] = useState({});
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleQuantityChange = (codigo, value) => {
    setQuantities((prev) => ({ ...prev, [codigo]: Number(value) }));
  };

  const handleAddToCart = (item, quantityToAdd) => {
    dispatch(addToCart({ ...item, quantity: Number(quantityToAdd) }));
  };

  const handleImageClick = (item) => {
    const cod = item.codigo.replace(/\//g, "|");
    navigate(`/product/${cod}`, { state: { item } });
  };

  if (products.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-300 bg-slate-50 px-4 py-8 text-center">
        <p className="text-lg font-semibold text-gray-700">No se encontraron resultados.</p>
        <p className="mt-1 text-sm text-gray-500">Probá con otra búsqueda o ajustá los filtros.</p>
      </div>
    );
  }

  return viewType === "table" ? (
    <ProductTable
      products={products}
      hoveredItem={hoveredItem}
      setHoveredItem={setHoveredItem}
      handleImageClick={handleImageClick}
      handleQuantityChange={handleQuantityChange}
      handleAddToCart={handleAddToCart}
      quantities={quantities}
      onProductsRefresh={onProductsRefresh}
    />
  ) : (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
      {products.map((item) => (
        <div key={item.codigo} className="h-full">
          <Product
            img={item.img}
            codigo={item.codigo}
            Descripcion={item.Descripcion}
            PrecioLista={item.PrecioLista}
            PrecioOferta={item.PrecioOferta}
            Stock={item.Stock}
            stock_mensaje={item.stock_mensaje}
            simbolo={item.simbolo}
            favorito={item.favorito}
          />
        </div>
      ))}
    </div>
  );
};

const Pagination = () => {
  const location = useLocation();
  const previousPathRef = useRef(location.pathname);
  const skipNextFetchRef = useRef(false);
  const currentPageStoragePath = sessionStorage.getItem("pageNumberPath");
  const [products, setProducts] = useState([]);
  const [pageNumber, setPageNumber] = useState(() =>
    currentPageStoragePath === location.pathname
      ? parseInt(sessionStorage.getItem("pageNumber")) || 1
      : 1
  );
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [selectedGroup, setSelectedGroup] = useState(() => sessionStorage.getItem("selectedGroup") || "0");
  const [selectedBrand, setSelectedBrand] = useState(() => sessionStorage.getItem("selectedBrand") || "0");
  const [selectedCondicion, setSelectedCondicion] = useState(() => sessionStorage.getItem("selectedCondicion") || "0");
  const [selectedType, setSelectedType] = useState(() => sessionStorage.getItem("selectedType") || "0");
  const [searchTerm, setSearchTerm] = useState(() => sessionStorage.getItem("searchTerm") || "");
  const [searchMode, setSearchMode] = useState(() => sessionStorage.getItem("searchMode") || "1");
  const [hoveredSearchMode, setHoveredSearchMode] = useState(null);
  const [viewType, setViewType] = useState("table");
  const [hoveredItem, setHoveredItem] = useState(null);
  const [loading, setLoading] = useState(false);

  const { config1, config2 } = useContext(UserConfigContext);
  const { ensureFavoritesLoaded } = useFavorites();
  const filtros = config1?.filtros || {};
  const atributos = config1?.atributos || [];
  const tipos = config1?.tipos || [];
  const grupos = config1?.grupos || [];
  const marcas = config1?.marcas || [];
  const vistaEcommerce = localStorage.getItem("vista_ecommerce") || "";

  useEffect(() => {
    if (location.pathname === "/Offer" || location.pathname === "/News") {
      setViewType("table");
      return;
    }

    if (vistaEcommerce) {
      setViewType(vistaEcommerce);
      return;
    }

    if (config2?.vista?.vista) {
      setViewType(config2.vista.vista);
    }
  }, [config2, location.pathname, vistaEcommerce]);

  const getLoadFunction = () => {
    switch (location.pathname) {
      case "/News":
        return loadNews;
      case "/Offer":
        return loadOffer;
      default:
        return loadProducts;
    }
  };

  const getMeta = () => {
    if (location.pathname === "/News") return paginationMeta2;
    if (location.pathname === "/Offer") return paginationMeta3;
    return paginationMeta;
  };

  useEffect(() => {
    if (previousPathRef.current === location.pathname) return;

    previousPathRef.current = location.pathname;
    skipNextFetchRef.current = true;
    setPageNumber(1);
    sessionStorage.setItem("pageNumber", 1);
    sessionStorage.setItem("pageNumberPath", location.pathname);
  }, [location.pathname]);

  const fetchProducts = useCallback(async ({ showLoading = true } = {}) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      const loadFunction = getLoadFunction();
      await loadFunction(pageNumber, itemsPerPage, searchTerm, selectedGroup, selectedType, selectedBrand, selectedCondicion, searchMode);
      const data =
        location.pathname === "/News"
          ? paginationItems2
          : location.pathname === "/Offer"
            ? paginationItems3
            : paginationItems;
      setProducts(data);
    } catch (error) {
      console.error("Error loading products:", error);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [location.pathname, pageNumber, itemsPerPage, searchTerm, selectedGroup, selectedType, selectedBrand, selectedCondicion, searchMode]);

  useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }

    const handler = setTimeout(() => {
      fetchProducts();
    }, 400);

    return () => clearTimeout(handler);
  }, [
    pageNumber,
    itemsPerPage,
    searchTerm,
    selectedGroup,
    selectedType,
    selectedBrand,
    selectedCondicion,
    location.pathname,
    fetchProducts,
    searchMode,
  ]);

  useEffect(() => {
    const loggedUserData = JSON.parse(localStorage.getItem("user"));
    if (products.length > 0 && loggedUserData?.data?.user?.id) {
      ensureFavoritesLoaded();
    }
  }, [products, ensureFavoritesLoaded]);

  const handleSearchTermChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPageNumber(1);
    sessionStorage.setItem("searchTerm", value);
    sessionStorage.setItem("pageNumber", 1);
    sessionStorage.setItem("pageNumberPath", location.pathname);
  };

  const handleSelectChange = (setter, key) => (e) => {
    const value = e.target.value;
    setter(value);
    sessionStorage.setItem(key, value);
    setPageNumber(1);
    sessionStorage.setItem("pageNumber", 1);
    sessionStorage.setItem("pageNumberPath", location.pathname);
  };

  const handlePageChange = ({ selected }) => {
    const newPage = selected + 1;
    setPageNumber(newPage);
    sessionStorage.setItem("pageNumber", newPage);
    sessionStorage.setItem("pageNumberPath", location.pathname);
  };

  const handleViewChange = (nextView) => {
    setViewType(nextView);
    localStorage.setItem("vista_ecommerce", nextView);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedGroup("0");
    setSelectedBrand("0");
    setSelectedCondicion("0");
    setSelectedType("0");
    setPageNumber(1);
    sessionStorage.setItem("searchTerm", "");
    sessionStorage.setItem("selectedGroup", "0");
    sessionStorage.setItem("selectedBrand", "0");
    sessionStorage.setItem("selectedCondicion", "0");
    sessionStorage.setItem("selectedType", "0");
    sessionStorage.setItem("pageNumber", 1);
    sessionStorage.setItem("pageNumberPath", location.pathname);
  };

  // ── selects más compactos: h-8 en vez de h-11, gap-1 en vez de gap-2 ──
  const renderFilterSelect = (label, value, onChange, options, optionValueKey, optionLabelKey) => (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</span>
      <select
        value={value}
        onChange={onChange}
        className="h-8 rounded-lg border border-gray-200 bg-white px-2 py-0 text-xs text-gray-700 shadow-sm outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
      >
        <option value="0">Todos</option>
        {options.map((option) => (
          <option key={option[optionValueKey]} value={option[optionValueKey]}>
            {option[optionLabelKey]}
          </option>
        ))}
      </select>
    </label>
  );

  const meta = getMeta();
  const pageCount = meta?.totalPages || 0;
  const totalItems = meta?.totalItems || 0;
  const hasActiveFilters = [
    searchTerm,
    selectedGroup,
    selectedBrand,
    selectedCondicion,
    selectedType,
  ].some((value) => value && value !== "0");

  const searchModeOptions = [
    {
      value: "1",
      label: "Frase",
      icon: FaQuoteLeft,
      tooltip: "La Palabra ingresada seleccionara los productos que tengan coincidencia dentro del Codigo, Descripcion e Informacion Adicional.",
    },
    {
      value: "2",
      label: "Multiple",
      icon: FaLayerGroup,
      tooltip: "Tomara cada palabra por separado y seleccionara los productos que tengan coincidencia dentro del Codigo, Descripcion e Informacion Adicional, en TODAS las palabras ingresadas.",
    },
    {
      value: "3",
      label: "Ampliada",
      icon: FaExpandAlt,
      tooltip: "Tomara cada palabra por separado y seleccionara los productos que tengan coincidencia dentro del Codigo, Descripcion e Informacion Adicional, en ALGUNA de las palabras ingresadas.",
    },
  ];

  return (
    // space-y-6 → space-y-3 | p-3 md:p-5 → p-2 md:p-3
    <div className="space-y-3 p-2 md:p-3">

      {/* ── Card catálogo: padding reducido ── */}
      <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white via-slate-50 to-blue-50/70 p-3 shadow-sm md:p-4">
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">Catalogo</p>
              <h2 className="text-xl font-bold text-gray-900">Listado de productos</h2>
              <p className="text-sm text-gray-500">
                {loading ? "Actualizando resultados..." : `${totalItems || products.length || 0} productos encontrados`}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
                <button
                  type="button"
                  onClick={() => handleViewChange("grid")}
                  className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                    viewType === "grid" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <FaBorderAll className="text-xs" />
                  Grilla
                </button>
                <button
                  type="button"
                  onClick={() => handleViewChange("table")}
                  className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                    viewType === "table" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <FaListUl className="text-xs" />
                  Tabla
                </button>
              </div>

              {/* select Mostrar: h-8 compacto */}
              <label className="flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 shadow-sm">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Mostrar</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setItemsPerPage(value);
                    setPageNumber(1);
                    sessionStorage.setItem("pageNumber", 1);
                  }}
                  className="bg-transparent text-xs text-gray-700 outline-none"
                >
                  <option value={50}>50</option>
                  <option value={75}>75</option>
                  <option value={100}>100</option>
                  <option value={-1}>Todos</option>
                </select>
              </label>
            </div>
          </div>

          <div className="grid gap-2 lg:grid-cols-[minmax(260px,1fr)_auto_minmax(220px,260px)] lg:items-center">
            {/* buscador: h-9 en vez de h-12 */}
            <label className="flex h-9 items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 shadow-sm transition max-w-full">
              <FaSearch className="text-sm text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por codigo o descripcion..."
                value={searchTerm}
                onChange={handleSearchTermChange}
                className="w-full bg-transparent text-sm text-gray-700 outline-none placeholder:text-gray-400"
              />
            </label>
            
            {/* indicador modo de filtros: py-2 → py-1.5 */}
            <div className="relative flex h-9 items-center justify-center gap-1 rounded-xl border border-gray-200 bg-white p-1 text-xs text-gray-600 shadow-sm">

              {searchModeOptions.map(({ value, label, icon: Icon }) => {
                const isActive = searchMode === value;
                return (
                  <label
                    key={value}
                    className="cursor-pointer whitespace-nowrap"
                    onMouseEnter={() => setHoveredSearchMode(value)}
                    onMouseLeave={() => setHoveredSearchMode(null)}
                    onFocus={() => setHoveredSearchMode(value)}
                    onBlur={() => setHoveredSearchMode(null)}
                  >
                    <input
                      type="radio"
                      name="tipoBusqueda"
                      value={value}
                      checked={isActive}
                      onChange={(e) => {
                        setSearchMode(e.target.value);
                        sessionStorage.setItem("searchMode", e.target.value);
                        setHoveredSearchMode(null);
                      }}
                      className="sr-only"
                    />
                    <span
                      className={`flex h-7 items-center gap-1.5 rounded-lg px-2.5 font-semibold transition ${
                        isActive
                          ? "bg-gray-900 text-white shadow-sm"
                          : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                      }`}
                    >
                      <Icon className="text-[11px]" />
                      {label}
                    </span>
                  </label>
                );
              })}
              {hoveredSearchMode && (
                <div className="pointer-events-none absolute left-1/2 top-11 z-30 w-72 -translate-x-1/2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-left text-xs font-medium leading-5 text-blue-900 shadow-lg">
                  {searchModeOptions.find((option) => option.value === hoveredSearchMode)?.tooltip}
                  <span className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 border-l border-t border-blue-100 bg-blue-50" />
                </div>
              )}
            </div>

            {/* indicador filtros: py-2 → py-1.5 */}
            <div className="flex h-9 items-center justify-between rounded-xl border border-dashed border-gray-300 bg-white/70 px-3 py-1.5 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <FaSlidersH className="text-gray-400" />
                <span>{hasActiveFilters ? "Filtros aplicados" : "Sin filtros activos"}</span>
              </div>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="font-semibold text-blue-600 transition hover:text-blue-700"
                >
                  Limpiar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Card filtros: padding más chico ── */}
      <div className="grid gap-3 rounded-2xl border border-gray-200 bg-white p-2 shadow-sm md:grid-cols-2 md:p-3 xl:grid-cols-4">
        {filtros?.condicion === 1 &&
          renderFilterSelect(
            "Condicion",
            selectedCondicion,
            handleSelectChange(setSelectedCondicion, "selectedCondicion"),
            atributos,
            "codigo",
            "descripcion"
          )}
        {filtros?.tipos === 1 &&
          renderFilterSelect(
            "Tipo",
            selectedType,
            handleSelectChange(setSelectedType, "selectedType"),
            tipos,
            "codigo",
            "descripcion"
          )}
        {filtros?.grupos === 1 &&
          renderFilterSelect(
            "Grupo",
            selectedGroup,
            handleSelectChange(setSelectedGroup, "selectedGroup"),
            grupos,
            "codigo",
            "descripcion"
          )}
        {filtros?.marcas === 1 &&
          renderFilterSelect(
            "Marca",
            selectedBrand,
            handleSelectChange(setSelectedBrand, "selectedBrand"),
            marcas,
            "clave_id",
            "descripcion"
          )}
      </div>

      {loading ? (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="h-40 animate-pulse rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-100 to-gray-50"
            />
          ))}
        </div>
      ) : (
        // ── Card productos: p-3, header interno más ajustado ──
        <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
          <Items
            products={products}
            viewType={viewType}
            hoveredItem={hoveredItem}
            setHoveredItem={setHoveredItem}
            onProductsRefresh={() => fetchProducts({ showLoading: false })}
          />
        </div>
      )}

      {pageCount > 1 && (
        <ReactPaginate
          nextLabel="Siguiente"
          previousLabel="Anterior"
          onPageChange={handlePageChange}
          pageCount={pageCount}
          forcePage={pageNumber - 1}
          containerClassName="flex flex-wrap justify-center gap-1.5 pt-1"
          pageClassName="min-w-[36px] rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-center text-sm text-gray-600 shadow-sm transition hover:border-gray-300 hover:bg-gray-50"
          previousClassName="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50"
          nextClassName="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50"
          breakClassName="px-2 py-1.5 text-sm text-gray-400"
          activeClassName="!border-gray-900 !bg-gray-900 !text-white"
          disabledClassName="pointer-events-none opacity-50"
        />
      )}
    </div>
  );
};

export default Pagination;
